import { GrowthBookProvider } from '@growthbook/growthbook-react';
import CssBaseline from '@mui/material/CssBaseline';
import GlobalStyles from '@mui/material/GlobalStyles';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import { Loader } from '@openx/components-core/lib/Loader/Loader.tsx';
import { SiteUnavailablePopup } from '@openx/components-core/lib/SiteUnavailablePopup/SiteUnavailablePopup.tsx';
import { SnackbarProvider, snakbarStyles } from '@openx/components-core/lib/Snackbar/SnackbarProvider.tsx';
import { theme } from '@openx/components-core/lib/theme/theme.ts';
import { globalStyles } from '@openx/constants/styles.ts';
import { useGrowthbook } from '@openx/utils/hooks/useGrowthbook.ts';
import { createDefaultGrowthBook } from '@openx/utils/lib/createDefaultGrowthBook.ts';
import { initializePendo } from '@openx/utils/lib/pendo/pendo.ts';
import type { Auth } from 'firebase/auth';
import type { SnackbarOrigin } from 'notistack';
import { useEffect, useRef, useState } from 'react';
import { ErrorBoundary } from './components/ErrorBoundary/ErrorBoundary.tsx';
import { APP_VERSION, GROWTHBOOK_API_HOST, GROWTHBOOK_API_KEY, GROWTHBOOK_NAME_ATTR, PENDO_API_KEY } from './config.ts';
import { getEnvironmentConfigs } from './environmentConfigs.ts';
import { getFirebaseAuth, initializeFirebase } from './firebaseIntegration/utils.ts';
import { DEFAULT_RELOAD_INTERVAL, useAppReload } from './hooks/useAppReload.ts';
import { GraphQLProvider } from './modules/graphql/GraphQLProvider.tsx';
import { Routes } from './routes.tsx';

const snackBarProps: SnackbarOrigin = {
  horizontal: 'left',
  vertical: 'bottom',
};

export const App = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [auth, setAuth] = useState<Auth | undefined>(undefined);

  // Default GrowthBook instance to prevent hook errors during loading
  const defaultGrowthBook = useRef(createDefaultGrowthBook());

  const { initialize: initializeGB, growthbook } = useGrowthbook({
    apiHost: GROWTHBOOK_API_HOST,
    clientKey: GROWTHBOOK_API_KEY,
    appName: GROWTHBOOK_NAME_ATTR,
  });

  // Initialize auto-reload hook (see useAppReload.ts for more details)
  useAppReload({
    interval: DEFAULT_RELOAD_INTERVAL,
    enabled: true,
    onBeforeReload: () => {
      // biome-ignore lint/suspicious/noConsole: logging info in console
      console.info('Preparing to reload app due to user inactivity');
    },
  });

  useEffect(() => {
    initializePendo(PENDO_API_KEY);
    initializeGB();

    const initAuth = async () => {
      try {
        const { firebase: firebaseConfig } = await getEnvironmentConfigs();
        const app = await initializeFirebase(firebaseConfig);
        const auth = await getFirebaseAuth(app);

        setAuth(auth);
      } catch (error) {
        // biome-ignore lint/suspicious/noConsole: logging error in console
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    initAuth();
    // biome-ignore lint/suspicious/noConsole: logging info in console
    console.info(`%cLogin version: ${APP_VERSION}`, 'font-weight:bold;');
  }, [initializeGB]);

  if (loading || !auth) {
    return <Loader />;
  }

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <SnackbarProvider anchorOrigin={snackBarProps}>
          <ErrorBoundary>
            <GraphQLProvider auth={auth}>
              <GlobalStyles styles={[globalStyles(theme), snakbarStyles(theme)]} />
              <CssBaseline />
              <SiteUnavailablePopup />
              <GrowthBookProvider growthbook={growthbook || defaultGrowthBook.current}>
                <Routes auth={auth} />
              </GrowthBookProvider>
            </GraphQLProvider>
          </ErrorBoundary>
        </SnackbarProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};
